import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';
import { CONFIG } from '../config';
import { buildUrl } from '../utils';
import { OcpApplianceRegistrationApi } from './OcpApplianceRegistrationApi';
import {
  UnregisterApplianceResponse,
  CasApplianceResponse,
  DamsApplianceResponse,
  EcpApplianceResponse,
  ResponseError,
  UserAppliancesResponse,
  RegisterApplianceResponse,
  UserCredentials,
  RegisterRequest,
  UnregisterRequest,
} from './types';

/** @public */
export class OcpApplianceRegistrationClient
  implements OcpApplianceRegistrationApi
{
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async postUserAppliances(
    credentials: UserCredentials,
  ): Promise<UserAppliancesResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `/auth-appliances`,
      }),
      credentials,
    );
    return response;
  }

  async searchApplianceByIdEcp(
    credentials: UserCredentials,
    applianceId: string,
  ): Promise<EcpApplianceResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `${applianceId}/ecp`,
      }),
      credentials,
    );
    return response;
  }

  async searchApplianceByIdCas(
    credentials: UserCredentials,
    applianceId: string,
  ): Promise<CasApplianceResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `${applianceId}/cas`,
      }),
      credentials,
    );
    return response;
  }

  async searchApplianceByIdDams(
    credentials: UserCredentials,
    applianceId: string,
  ): Promise<DamsApplianceResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `${applianceId}/cbs`,
      }),
      credentials,
    );
    return response;
  }

  async unregisterAppliance(
    request: UnregisterRequest,
    applianceId: string,
  ): Promise<UnregisterApplianceResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `${applianceId}/unregister`,
      }),
      request,
    );
    return response;
  }

  async registerAppliance(
    request: RegisterRequest,
    applianceId: string,
  ): Promise<RegisterApplianceResponse> {
    const response = await this.post(
      buildUrl(CONFIG.baseUrl, {
        path: `${applianceId}`,
      }),
      request,
    );
    return response;
  }

  async get(path: string): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    });

    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const body = await response.json();
    return { status: response.status, body };
  }

  async post(path: string, payload: { [key: string]: any }): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'POST',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    let body = {};
    if (isJson) {
      const text = await response.text();
      try {
        body = text ? JSON.parse(text) : {};
      } catch (error) {
        body = {};
      }
    }

    return { status: response.status, body };
  }

  async delete(path: string, payload: { [key: string]: any }): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('proxy')}${path}`;
    const { token: idToken } = await this.identityApi.getCredentials();
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      method: 'DELETE',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = response;
      const message = `Request failed with ${response.status} ${response.statusText}, ${body}`;
      throw new ResponseError(message, response);
    }

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    let body = {};
    if (isJson) {
      const text = await response.text();
      try {
        body = text ? JSON.parse(text) : {};
      } catch (error) {
        body = {};
      }
    }

    return { status: response.status, body };
  }
}
